import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, PublicSeller } from '@outlaw-oasis/types';
import { api } from '../lib/api';
import { useCartStore } from './cartStore';

interface AuthState {
  user: User | null;
  seller: PublicSeller | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; username: string; password: string; displayName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

interface AuthResponse {
  user: User;
  seller?: PublicSeller;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      seller: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await api.post<AuthResponse>('/auth/login', { email, password });
        const { user, seller } = response.data!;
        set({ user, seller: seller ?? null, isAuthenticated: true });
        // Merge guest cart with user cart after login
        await useCartStore.getState().mergeGuestCart();
      },

      register: async (data) => {
        const response = await api.post<AuthResponse>('/auth/register', data);
        const { user } = response.data!;
        set({ user, isAuthenticated: true });
        // Merge guest cart with user cart after registration
        await useCartStore.getState().mergeGuestCart();
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // Ignore errors
        }
        // Clear persisted storage before reload
        localStorage.removeItem('auth-storage');
        // Hard reload — clears all in-memory state and re-runs checkAuth
        // against the now-revoked session, confirming logged-out state
        window.location.href = '/';
      },

      checkAuth: async () => {
        try {
          const response = await api.get<AuthResponse>('/auth/me');
          const { user, seller } = response.data!;
          set({ user, seller: seller ?? null, isAuthenticated: true, isLoading: false });
        } catch {
          set({ user: null, seller: null, isAuthenticated: false, isLoading: false });
        }
      },

      refreshToken: async () => {
        try {
          await api.post('/auth/refresh');
        } catch {
          set({ user: null, seller: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ isAuthenticated: state.isAuthenticated }),
    }
  )
);
